import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { Ellipsis } from 'lucide-react';
import React, { useState } from 'react';

const MemoizedIconButton = React.memo(IconButton);

export interface IItemAction {
    id: string;
    icon: React.ReactNode;
    text: string;
    helper?: string;
    onClick?: () => void;
}

export const ItemActions: React.FC<{ actions: IItemAction[] }> = ({ actions }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const openMenu = React.useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
        },
        [setAnchorEl],
    );

    const closeMenu = React.useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const menuItems = React.useMemo(() => {
        return actions.slice(1).map((action) => (
            <MenuItem
                key={action.id}
                onClick={() => {
                    action.onClick?.();
                    closeMenu();
                }}
                sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}
            >
                {action.icon} {action.text}
            </MenuItem>
        ));
    }, [actions, closeMenu]);

    return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            {actions.length > 0 && <ActionButton action={actions[0]} />}
            {actions.length === 2 && <ActionButton action={actions[1]} />}
            {actions.length > 2 && (
                <MemoizedIconButton size="small" sx={{ p: 0 }} onClick={openMenu}>
                    <Ellipsis size={16} />
                </MemoizedIconButton>
            )}
            {anchorEl && (
                <Menu anchorEl={anchorEl} keepMounted onClose={closeMenu} open={Boolean(anchorEl)}>
                    {menuItems}
                </Menu>
            )}
        </Box>
    );
};

export const ActionButton: React.FC<{ action: IItemAction }> = ({ action }) => {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip title={action.text} enterDelay={500} enterNextDelay={500}>
                <IconButton aria-label={action.text} size="small" sx={{ p: 0 }} onClick={action.onClick}>
                    {action.icon}
                </IconButton>
            </Tooltip>
        </Box>
    );
};
